/**
 * CierreTicketPreview.tsx — Vista previa del cierre de caja (web + térmica).
 *
 * Vista Web: diseño ticket React (`CierreTicketWebBody`) — compartir/imagen.
 * Vista Térmica: `ThermalTicketSimulator` + preview ESC/POS (`decodeEscPosToPreviewLines`).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, Lock, Printer, ShieldAlert, X } from "lucide-react";
import { txRead } from "@/lib/persistence/transactionalStore";
import type { CierreCaja, Venta } from "../types/caja.types";
import type { EmpleadoNombre } from "../utils/getEmpleadoNombre";
import type { CierreShareDocumentKind } from "../utils/buildCierreShareCaption";
import {
  formatCierreDateShortAmPm,
  formatCierreHeaderDateAmPm,
} from "../utils/cierreCuadranteHelper";
import { formatCierreDuration } from "../utils/cierreHelpers";
import { readCreditosSnapshotForSesion } from "../utils/readCreditosSnapshotForSesion";
import { buildCierreTicketModel } from "../utils/buildCierreTicketModel";
import {
  resolveCierreForTicketDemo,
  resolveVentasForCierreDemo,
} from "../demo/resolveCierreDemoData";
import {
  isPrinterUnavailable,
  tryPrintThermalCierre,
} from "@/lib/native/posHardware";
import { askPrintWithoutPrinter } from "@/store/printerGateStore";
import { buildCierrePreviewLines } from "@/features/caja/utils/cierreTicketEscPos";
import { ThermalTicketSimulator } from "@/features/caja/components/ThermalTicketSimulator";
import { TicketPreviewWebFrame } from "@/features/caja/components/TicketPreviewWebFrame";
import { TICKET_PREVIEW_SURFACE_CLASS } from "@/features/caja/constants/ticketTornPaper";
import {
  TICKET_PREVIEW_MODAL_WIDTH_CLASS,
  TicketThermalViewControls,
} from "@/features/caja/components/TicketThermalViewControls";
import { useTicketThermalViewState } from "@/features/caja/hooks/useTicketThermalViewState";
import { ShareCierreActions } from "./ShareCierreActions";
import { CierreTicketWebBody } from "./CierreTicketWebBody";

const CIERRE_WEB_CAPTURE_WIDTH_PX = 448;
const CIERRE_WEB_CAPTURE_SCALE = 2;

interface BusinessInfo {
  nombre: string;
  slogan?: string;
  direccion?: string;
  nit?: string;
}

const DEFAULT_BUSINESS: BusinessInfo = {
  nombre: "ALTENOT",
  slogan: "Sistema de Control Total",
  direccion: "",
  nit: "",
};

interface CierreTicketPreviewProps {
  cierre: CierreCaja;
  ventas: Venta[];
  empleadosCatalog?: EmpleadoNombre[];
  defaultPhone?: string;
  onClose: () => void;
  shareMenuHeader?: string;
  shareTriggerLabel?: string;
  enforceShare?: boolean;
  onShared?: () => void;
  variant?: CierreShareDocumentKind;
}

export const CierreTicketPreview = ({
  cierre,
  ventas,
  empleadosCatalog,
  defaultPhone,
  onClose,
  shareMenuHeader,
  shareTriggerLabel,
  enforceShare = false,
  onShared,
  variant = "cierre",
}: CierreTicketPreviewProps) => {
  const isParcial = variant === "parcial";
  /** ESC/POS solo distingue cierre vs parcial; jornada usa layout de cierre. */
  const thermalVariant: "cierre" | "parcial" =
    variant === "parcial" ? "parcial" : "cierre";
  const documentTitle = isParcial ? "Reporte Parcial" : "Cierre de Caja";
  const modalHeaderTitle = isParcial ? "Reporte Parcial" : "Imagen del Cierre";
  const shareTriggerLabelResolved =
    shareTriggerLabel ?? (isParcial ? "Compartir reporte" : "Compartir cierre");
  const shareMenuHeaderResolved =
    shareMenuHeader ??
    (isParcial ? "Compartir reporte por" : "Enviar cierre por");
  const ticketRef = useRef<HTMLDivElement>(null);
  const { viewMode, setViewMode, paperWidthMm, setPaperWidthMm } =
    useTicketThermalViewState("web");
  const [business, setBusiness] = useState<BusinessInfo>(DEFAULT_BUSINESS);

  useEffect(() => {
    try {
      const saved = txRead<Partial<BusinessInfo>>("businessConfig", {});
      setBusiness({ ...DEFAULT_BUSINESS, ...saved });
    } catch {
      /* mantener defaults */
    }
  }, []);

  const cierreResuelto = useMemo(
    () => resolveCierreForTicketDemo(cierre),
    [cierre],
  );

  const ventasResueltas = useMemo(
    () => resolveVentasForCierreDemo(cierreResuelto, ventas),
    [cierreResuelto, ventas],
  );

  const ticketModel = useMemo(
    () => buildCierreTicketModel(cierreResuelto, ventasResueltas),
    [cierreResuelto, ventasResueltas],
  );

  const creditosSesion = useMemo(() => {
    if (Array.isArray(cierreResuelto.creditosSnapshot)) {
      return [...cierreResuelto.creditosSnapshot].sort(
        (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
      );
    }
    return readCreditosSnapshotForSesion(cierreResuelto.sesionId).sort(
      (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
    );
  }, [cierreResuelto.creditosSnapshot, cierreResuelto.sesionId]);

  const thermalPreviewLines = useMemo(
    () =>
      buildCierrePreviewLines({
        cierre: { ...cierreResuelto, creditosSnapshot: creditosSesion },
        ventas: ventasResueltas,
        paperWidthMm,
        variant: thermalVariant,
      }),
    [
      cierreResuelto,
      ventasResueltas,
      creditosSesion,
      paperWidthMm,
      thermalVariant,
    ],
  );

  const handlePrint = async () => {
    // Sin impresora: se pregunta antes de enviar el evento de impresión.
    if (isPrinterUnavailable()) {
      if (await askPrintWithoutPrinter("el comprobante de cierre")) {
        window.print();
      }
      return;
    }
    const sent = await tryPrintThermalCierre({
      cierre: cierreResuelto,
      ventas: ventasResueltas,
      variant: thermalVariant,
    });
    if (!sent) window.print();
  };

  const ticketDateLabel = formatCierreHeaderDateAmPm(
    cierreResuelto.cerradaEn || cierreResuelto.abiertaEn,
  );

  return (
    <div
      className={`fixed inset-0 ${enforceShare ? "z-[1000]" : "z-[60]"} flex items-center justify-center p-2 sm:p-4 animate-fade-in`}
      onKeyDown={(e) => {
        if (enforceShare && e.key === "Escape") e.preventDefault();
      }}
    >
      {enforceShare ? (
        <div
          className="no-print absolute inset-0 bg-black/80 backdrop-blur-sm"
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : (
        <button
          type="button"
          aria-label="Cerrar vista previa"
          className="no-print absolute inset-0 bg-black/60 backdrop-blur-sm border-0 cursor-default"
          onClick={onClose}
        />
      )}
      <div
        className={`ticket-print-card ticket-print-scope relative z-10 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full ${TICKET_PREVIEW_MODAL_WIDTH_CLASS} flex flex-col overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] my-2 sm:my-0`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="no-print shrink-0 p-2 sm:p-4 bg-gradient-to-r from-primary-600 to-primary-700 flex items-center justify-between gap-2">
          <h2 className="text-[10px] leading-tight sm:text-sm md:text-xl font-bold text-white flex items-center gap-1.5 sm:gap-2 min-w-0">
            {enforceShare ? (
              <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-6 shrink-0" />
            ) : (
              <Archive className="w-3.5 h-3.5 sm:w-4 sm:h-6 shrink-0" />
            )}
            <span className="min-w-0 break-words">
              {enforceShare
                ? isParcial
                  ? "Compartir Reporte"
                  : "Compartir Cierre"
                : modalHeaderTitle}
            </span>
          </h2>
          {!enforceShare && (
            <button
              onClick={onClose}
              className="p-1 sm:p-2 hover:bg-white/20 rounded-full transition-colors shrink-0"
              aria-label="Cerrar"
            >
              <X size={16} className="text-white sm:hidden" />
              <X size={20} className="text-white hidden sm:block" />
            </button>
          )}
        </div>

        {enforceShare && (
          <div className="no-print shrink-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/70 flex items-start gap-2">
            <ShieldAlert
              size={15}
              className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
            />
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs font-bold text-amber-900 dark:text-amber-100 uppercase tracking-wide leading-snug">
                La plataforma quedó bloqueada
              </p>
              <p className="text-[9px] sm:text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
                Para seguir usando ALTENOT debes compartir este cierre con el
                dueño. Elige un método (WhatsApp, PDF, Gmail o descarga) y
                confirma con <strong className="font-bold">«Sí, listo»</strong>.
                El bloqueo solo se libera cuando compartas o descargues el
                reporte.
              </p>
            </div>
          </div>
        )}

        <TicketThermalViewControls
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          paperWidthMm={paperWidthMm}
          onPaperWidthChange={setPaperWidthMm}
        />

        <div className={TICKET_PREVIEW_SURFACE_CLASS}>
          <div
            ref={ticketRef}
            className="w-full min-w-0"
            data-ticket-export-root={viewMode === "web" ? "" : undefined}
            data-capture-scale={
              viewMode === "web" ? String(CIERRE_WEB_CAPTURE_SCALE) : undefined
            }
            data-capture-width={
              viewMode === "web"
                ? String(CIERRE_WEB_CAPTURE_WIDTH_PX)
                : undefined
            }
          >
            {viewMode === "thermal" ? (
              <ThermalTicketSimulator
                lines={thermalPreviewLines.map((ln) => ln.text)}
                previewLines={thermalPreviewLines}
                paperWidthMm={paperWidthMm}
              />
            ) : (
              <TicketPreviewWebFrame shellClassName="text-[9px] leading-snug sm:text-sm sm:leading-normal text-gray-900">
                <section className="border border-solid border-gray-800 mb-0">
                  <div className="text-center flex flex-col items-center gap-0.5 sm:gap-1 px-2 py-2 sm:px-3 sm:py-3">
                    <h1 className="text-base sm:text-xl font-black uppercase tracking-wider w-full text-center">
                      {business.nombre}
                    </h1>
                    {business.slogan && (
                      <p className="text-[8px] sm:text-[10px] font-bold w-full text-center">
                        {business.slogan}
                      </p>
                    )}
                    {business.direccion && (
                      <p className="text-[8px] sm:text-[10px] w-full text-center mt-0.5">
                        {business.direccion}
                      </p>
                    )}
                    {business.nit && (
                      <p className="text-[8px] sm:text-[10px] w-full text-center">
                        NIT: {business.nit}
                      </p>
                    )}
                  </div>

                  <div className="border-t border-solid border-gray-800 text-center px-2 py-2 sm:px-3 sm:py-3">
                    <p className="text-sm sm:text-base font-black uppercase tracking-widest">
                      {documentTitle}
                    </p>
                    {cierreResuelto.label && (
                      <p className="text-[8px] sm:text-[10px] font-bold text-emerald-700 mt-0.5 whitespace-pre-line">
                        {cierreResuelto.label}
                      </p>
                    )}
                    {cierreResuelto.puntoCajaNombre && (
                      <p className="text-[8px] sm:text-[10px] mt-0.5">
                        {cierreResuelto.puntoCajaNombre}
                      </p>
                    )}
                    <p className="text-[8px] sm:text-[10px] font-mono mt-0.5 sm:mt-1 w-full text-center">
                      {ticketDateLabel}
                    </p>
                  </div>
                  <div className="border-t border-solid border-gray-800 px-2 py-2 sm:px-3 sm:py-3 text-[9px] sm:text-[11px] space-y-1">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
                      <span>Apertura:</span>
                      <strong>
                        {formatCierreDateShortAmPm(cierreResuelto.abiertaEn)}
                      </strong>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
                      <span>{isParcial ? "Reporte:" : "Cierre:"}</span>
                      <strong>
                        {cierreResuelto.cerradaEn
                          ? formatCierreDateShortAmPm(cierreResuelto.cerradaEn)
                          : ticketDateLabel}
                      </strong>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
                      <span>Duración:</span>
                      <strong>{formatCierreDuration(cierreResuelto)}</strong>
                    </div>
                    {cierreResuelto.openedByName ? (
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
                        <span>Abrió:</span>
                        <strong>{cierreResuelto.openedByName}</strong>
                      </div>
                    ) : null}
                    {cierreResuelto.closedByName ? (
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
                        <span>{isParcial ? "Generó:" : "Cerró:"}</span>
                        <strong>{cierreResuelto.closedByName}</strong>
                      </div>
                    ) : null}
                  </div>
                </section>

                <CierreTicketWebBody
                  cierre={cierreResuelto}
                  ventas={ventasResueltas}
                  model={ticketModel}
                  creditosSesion={creditosSesion}
                  empleadosCatalog={empleadosCatalog}
                  isParcial={isParcial}
                />

                <div className="text-center pt-1.5 sm:pt-2 mt-1 sm:mt-1.5 border-t border-solid border-gray-300">
                  <p className="text-[7px] sm:text-[9px] text-gray-400 tracking-widest uppercase">
                    Gestionado con
                  </p>
                  <p className="text-[8px] sm:text-[10px] font-black text-gray-600 tracking-wider uppercase">
                    ALTENOT
                  </p>
                  <p className="text-[7px] sm:text-[9px] text-gray-500 italic">
                    Imagen no editable — comprobante oficial
                  </p>
                </div>
              </TicketPreviewWebFrame>
            )}
          </div>
        </div>

        <div className="app-fixed-bottom no-print shrink-0 p-2 sm:p-4 sm:pb-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col gap-1.5 sm:gap-3">
          <ShareCierreActions
            cierre={cierreResuelto}
            businessName={business.nombre}
            targetRef={ticketRef}
            defaultPhone={defaultPhone}
            menuHeader={shareMenuHeaderResolved}
            triggerLabel={shareTriggerLabelResolved}
            shareKind={variant}
            requireShareConfirmation={enforceShare}
            onShared={onShared}
          />
          <div className="flex gap-2 sm:gap-3">
            {!enforceShare && (
              <button
                onClick={onClose}
                className="flex-1 px-2 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 rounded-lg sm:rounded-xl font-black text-gray-700 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors uppercase text-[10px] sm:text-xs md:text-sm tracking-tighter"
              >
                Cerrar
              </button>
            )}
            <button
              onClick={() => void handlePrint()}
              className={`${enforceShare ? "w-full" : "flex-1"} px-2 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 rounded-lg sm:rounded-xl font-black text-white bg-primary-600 hover:bg-primary-700 transition-colors flex items-center justify-center gap-1 sm:gap-2 uppercase text-[10px] sm:text-xs md:text-sm tracking-tighter shadow-lg shadow-primary-500/30`}
            >
              <Printer className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 shrink-0" />{" "}
              Imprimir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
